import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

/**
 * Every topic name in the org.
 *
 * The fast half of the org topic catalog, and the one most callers want:
 * `[[topic]]` references resolve against names alone. Reach for this by
 * default and add `useOrgMessagePaths` only if you need the corpus too —
 * that endpoint 504s for the largest orgs (ENG-2818), and these were once a
 * single request, so its timeouts took topic names down with them.
 */
export default function useOrgTopicNames(
  orgId?: string,
  opts?: { enabled?: boolean },
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { topics: topicService } = useDomainServices();

  return useQuery<string[], Error>({
    queryKey: ["orgTopicNames", canonicalOrgId],
    queryFn: ({ signal }) => {
      if (canonicalOrgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }

      return topicService.getTopicNamesForOrg({
        abortSignal: signal,
        resourceOwnerId: canonicalOrgId,
      });
    },
    enabled: canonicalOrgId !== undefined && (opts?.enabled ?? true),
    staleTime: 30000,
  });
}
